// ============================================================================
// ticketFiscal.js — Comprobante impreso (ticket térmico 58/80 mm y A4)
// ----------------------------------------------------------------------------
// Fuente única del HTML que imprimen VentasPOS, Facturacion, VentasHistorial y
// PedidosWeb. Antes había cuatro copias que divergían (una calculaba el IVA por
// alícuota real y otra lo fijaba en 21%; una imprimía "Documento no válido como
// factura" sobre una factura con CAE; el QR de cada una armaba el JSON distinto).
//
// Módulo PURO (sin supabase ni React): cada pantalla trae los datos y los pasa
// con la forma de las filas de la base:
//   sale    -> fila de `sales` (cae, cae_vencimiento, numero_factura,
//              invoice_cbte_tipo, invoice_type_label, invoice_iva, fecha_*, ...)
//   items   -> filas de `sale_items` (quantity, unit_price, subtotal,
//              iva_percentage, combo_role, product_name_snapshot, products{...})
//   client  -> fila de `clients` (full_name, doc_type, doc_number, tax_condition, address)
//   emitter -> resolveTicketEmitter({ organization, billingEntity })
//
// Requisitos que cubre (RG 1415 / RG 4291 / Ley 27.743):
//   - Encabezado del emisor: razón social, CUIT, IIBB, domicilio comercial,
//     inicio de actividades y condición frente al IVA.
//   - Tipo de comprobante real (letra + código), punto de venta y número.
//   - Factura A: neto gravado e IVA discriminado por alícuota.
//   - Factura B a consumidor final: bloque "Régimen de Transparencia Fiscal al
//     Consumidor (Ley 27.743)" con el IVA contenido. Obligatorio, no depende de
//     la configuración.
//   - Factura C: nunca muestra IVA.
//   - CAE + vencimiento y QR legible (centrado, ~38 mm en 80 mm).
//
// OJO fechas: `fecha_facturacion` es `date` y `cae_vencimiento` es timestamptz
// con medianoche UTC (viene de ARCA como fecha) -> ambos van con
// formatDateOnlyValue. La hora sale de `invoice_sent_at`, que es un instante.
// ============================================================================

import {
  afipPercentFromIvaId,
  buildAfipIvaBreakdown,
  getItemIvaPercent,
  round2,
  selectBillableItems,
} from './afipIva.js';
import { getSaleItemDisplayName } from './productLabels.js';
import {
  formatDateOnlyValue,
  formatOperationTime,
  getOperationDateKey,
} from './localDateTime.js';

// --- Tipos de comprobante ARCA ----------------------------------------------
export const CBTE_TIPOS = {
  1: { label: 'FACTURA', letra: 'A' },
  2: { label: 'NOTA DE DÉBITO', letra: 'A' },
  3: { label: 'NOTA DE CRÉDITO', letra: 'A' },
  6: { label: 'FACTURA', letra: 'B' },
  7: { label: 'NOTA DE DÉBITO', letra: 'B' },
  8: { label: 'NOTA DE CRÉDITO', letra: 'B' },
  11: { label: 'FACTURA', letra: 'C' },
  12: { label: 'NOTA DE DÉBITO', letra: 'C' },
  13: { label: 'NOTA DE CRÉDITO', letra: 'C' },
  51: { label: 'FACTURA', letra: 'M' },
  52: { label: 'NOTA DE DÉBITO', letra: 'M' },
  53: { label: 'NOTA DE CRÉDITO', letra: 'M' },
};

const NO_VALIDO_RE = /no\s+v[aá]lido\s+como\s+factura/i;

export const escapeTicketHtml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

export const formatTicketMoney = (value) =>
  Number(value || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatQty = (value) => {
  const n = Number(value || 0);
  if (Number.isInteger(n)) return String(n);
  return n.toLocaleString('es-AR', { maximumFractionDigits: 3 });
};

const formatRate = (rate) =>
  Number(rate).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const onlyDigits = (value) => String(value ?? '').replace(/\D/g, '');

export const formatCuit = (value) => {
  const d = onlyDigits(value);
  return d.length === 11 ? `${d.slice(0, 2)}-${d.slice(2, 10)}-${d.slice(10)}` : String(value || '');
};

// '4-8837' | '0004-00008837' -> '0004-00008837' (ptoVta de hasta 5 dígitos).
export const formatComprobanteNumero = (numero) => {
  const raw = String(numero || '').trim();
  const m = raw.match(/^(\d{1,5})\s*-\s*(\d{1,8})$/);
  if (!m) return raw;
  return `${m[1].padStart(4, '0')}-${m[2].padStart(8, '0')}`;
};

const parseNumero = (numero) => {
  const m = String(numero || '').match(/^(\d{1,5})\s*-\s*(\d{1,8})$/);
  return m ? { ptoVta: Number(m[1]), nro: Number(m[2]) } : { ptoVta: 0, nro: 0 };
};

export const isFiscalSale = (sale) => Boolean(sale?.cae);

// Tipo/letra del comprobante emitido. Sin CAE es un ticket interno ("X").
export const getComprobanteInfo = (sale) => {
  if (!isFiscalSale(sale)) {
    return { fiscal: false, codigo: null, label: 'TICKET', letra: 'X' };
  }
  const codigo = Number(sale?.invoice_cbte_tipo) || null;
  const known = codigo ? CBTE_TIPOS[codigo] : null;
  if (known) return { fiscal: true, codigo, label: known.label, letra: known.letra };

  // Sin código: lo deducimos del label guardado ("FACTURA B").
  const label = String(sale?.invoice_type_label || 'FACTURA').toUpperCase().trim();
  const letraMatch = label.match(/\b([ABCM])$/);
  return {
    fiscal: true,
    codigo,
    label: label.replace(/\s+[ABCM]$/, '') || 'FACTURA',
    letra: letraMatch ? letraMatch[1] : '',
  };
};

// Datos del emisor: la entidad de facturación manda; la organización completa
// lo que la entidad no tenga.
export const resolveTicketEmitter = ({ organization = null, billingEntity = null } = {}) => {
  const org = organization || {};
  const be = billingEntity || {};
  return {
    fantasia: org.name || be.tax_name || '',
    razonSocial: be.tax_name || org.legal_name || org.name || '',
    cuit: be.cuit || org.cuit || '',
    condicionIva: be.fiscal_configuration || be.tax_condition || org.tax_condition || '',
    domicilio: be.domicilio_comercial || org.full_address || org.address || '',
    iibb: be.iibb || '',
    inicioActividades: be.inicio_actividades || '',
    telefono: org.phone || '',
  };
};

// Tipo de documento del receptor para el QR (tabla ARCA): 80 CUIT, 86 CUIL,
// 96 DNI, 99 sin identificar.
const resolveDocRec = (client) => {
  const nro = onlyDigits(client?.doc_number);
  if (!nro || Number(nro) === 0) return { tipo: 99, nro: 0 };
  const type = String(client?.doc_type || '').toUpperCase();
  if (type === 'CUIT') return { tipo: 80, nro: Number(nro) };
  if (type === 'CUIL') return { tipo: 86, nro: Number(nro) };
  if (type === 'DNI') return { tipo: 96, nro: Number(nro) };
  if (nro.length === 11) return { tipo: 80, nro: Number(nro) };
  if (nro.length >= 7 && nro.length <= 8) return { tipo: 96, nro: Number(nro) };
  return { tipo: 99, nro: 0 };
};

// Fecha de emisión como 'YYYY-MM-DD'.
const resolveEmissionDateKey = (sale) =>
  String(sale?.fecha_facturacion || '').slice(0, 10)
  || getOperationDateKey(sale?.invoice_sent_at)
  || String(sale?.fecha_venta || '').slice(0, 10)
  || getOperationDateKey(sale?.created_at);

// URL del QR (RG 4291). Devuelve null si la venta no tiene CAE.
export const buildAfipQrUrl = ({ sale, emitterCuit, client = null, ptoVentaFallback = null }) => {
  if (!sale?.cae) return null;
  const { ptoVta, nro } = parseNumero(sale.numero_factura);
  const doc = resolveDocRec(client || sale.clients);
  const payload = {
    ver: 1,
    fecha: resolveEmissionDateKey(sale),
    cuit: Number(onlyDigits(emitterCuit)) || 0,
    ptoVta: ptoVta || Number(ptoVentaFallback) || 0,
    tipoCmp: Number(sale.invoice_cbte_tipo) || 0,
    nroCmp: nro,
    importe: round2(Number(sale.total || 0)),
    moneda: 'PES',
    ctz: 1,
    tipoDocRec: doc.tipo,
    nroDocRec: doc.nro,
    tipoCodAut: 'E',
    codAut: Number(onlyDigits(sale.cae)),
  };
  const json = JSON.stringify(payload);
  const b64 = typeof btoa === 'function' ? btoa(json) : Buffer.from(json, 'utf8').toString('base64');
  return `https://www.afip.gob.ar/fe/qr/?p=${b64}`;
};

const parseInvoiceIva = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    try { const parsed = JSON.parse(value); return Array.isArray(parsed) ? parsed : []; } catch { return []; }
  }
  return [];
};

// Desglose de IVA por alícuota. Prioriza lo que se envió a ARCA
// (sales.invoice_iva); si no está (venta vieja o POS antes de refrescar),
// lo recalcula con el mismo motor que arma el request (buildAfipIvaBreakdown).
export const buildIvaDesglose = ({ sale, items, total }) => {
  const fromAfip = parseInvoiceIva(sale?.invoice_iva);
  const source = fromAfip.length
    ? fromAfip
    : buildAfipIvaBreakdown(items, { saleTotal: Number(total ?? sale?.total ?? 0) }).iva;

  const rows = source
    .map((r) => ({
      rate: afipPercentFromIvaId(r.Id),
      base: round2(Number(r.BaseImp || 0)),
      iva: round2(Number(r.Importe || 0)),
    }))
    .filter((r) => r.iva > 0)
    .sort((a, b) => a.rate - b.rate);

  const totalIva = round2(rows.reduce((acc, r) => acc + r.iva, 0));
  const totalNeto = round2(rows.reduce((acc, r) => acc + r.base, 0));
  return { rows, totalIva, totalNeto };
};

// Descuento propio de la línea (sale_items.list_unit_price / item_discount_*).
// null si la línea no tiene descuento por producto.
const lineItemDiscount = (it) => {
  const pct = Number(it?.item_discount_pct);
  const lista = Number(it?.list_unit_price);
  const monto = Number(it?.item_discount_amount);
  if (!(pct > 0) || !(lista > 0) || !(monto > 0)) return null;
  return { pct: Math.round(pct * 100) / 100, lista, monto: round2(monto) };
};

const lineGross = (it) => {
  const sub = Number(it?.subtotal);
  if (Number.isFinite(sub) && sub > 0) return sub;
  return Number(it?.quantity || 0) * Number(it?.unit_price || 0);
};

const resolveWidth = (printerWidth) => {
  if (printerWidth === '58mm') return { page: '58mm', thermal: true, qr: '30mm', font: '11px', pad: '2mm' };
  if (printerWidth === 'a4') return { page: '210mm', thermal: false, qr: '40mm', font: '12px', pad: '12mm' };
  return { page: '80mm', thermal: true, qr: '38mm', font: '12px', pad: '3mm' };
};

const isGenericConsumidorFinal = (client) => {
  const name = String(client?.full_name || '').trim().toLowerCase();
  return !name || name === 'consumidor final';
};

/**
 * HTML completo del comprobante.
 * @param {object} p
 * @param {object} p.sale       fila de sales
 * @param {Array}  p.items      filas de sale_items
 * @param {object} [p.client]   fila de clients (default: sale.clients)
 * @param {object} p.emitter    resolveTicketEmitter(...)
 * @param {object} [p.config]   print_config.factura / ticket_venta
 * @param {string} [p.qrDataUrl] imagen del QR (data URL)
 * @param {Array}  [p.payments] [{ label, amount }] (default: método de la venta por el total)
 * @param {number} [p.change]   vuelto, si se conoce
 * @param {string} [p.sellerName]
 * @param {string} [p.siteName]
 * @param {string} [p.internalNumber] número a mostrar en tickets sin CAE
 */
export const buildFiscalTicketHtml = ({
  sale = {},
  items = [],
  client = null,
  emitter = {},
  config = {},
  qrDataUrl = null,
  payments = null,
  change = null,
  sellerName = '',
  siteName = '',
  internalNumber = '',
} = {}) => {
  const e = escapeTicketHtml;
  const cfg = config || {};
  const w = resolveWidth(cfg.printerWidth);
  const cli = client || sale.clients || null;
  const cbte = getComprobanteInfo(sale);
  const total = round2(Number(sale.total || 0));
  const discrimina = cbte.fiscal && (cbte.letra === 'A' || cbte.letra === 'M');
  const transparencia = cbte.fiscal && cbte.letra === 'B';
  const muestraAlicuota = cbte.fiscal && cbte.letra !== 'C';

  // --- Encabezado del emisor ---
  const headerTitle = cfg.headerText || emitter.fantasia || emitter.razonSocial || 'Comprobante';
  const headerLines = [];
  if (emitter.razonSocial && emitter.razonSocial.trim().toLowerCase() !== headerTitle.trim().toLowerCase()) {
    headerLines.push(e(emitter.razonSocial));
  }
  if (emitter.cuit) headerLines.push(`C.U.I.T. Nro.: ${e(formatCuit(emitter.cuit))}`);
  if (emitter.iibb) headerLines.push(`Ing. Brutos: ${e(emitter.iibb)}`);
  if (emitter.domicilio) headerLines.push(e(emitter.domicilio));
  if (emitter.telefono) headerLines.push(`Tel: ${e(emitter.telefono)}`);
  if (emitter.inicioActividades) headerLines.push(`Inicio de actividades: ${e(formatDateOnlyValue(emitter.inicioActividades))}`);
  if (emitter.condicionIva) headerLines.push(e(String(emitter.condicionIva).toUpperCase()));

  // Subtítulo: en un comprobante con CAE nunca puede decir "no válido como factura".
  const rawSub = cfg.subHeaderText ?? '';
  const subHeader = cbte.fiscal
    ? (NO_VALIDO_RE.test(rawSub) ? '' : rawSub)
    : (rawSub || 'Documento no válido como factura');

  const logo = cfg.showLogo && cfg.logoBase64
    ? `<img src="${cfg.logoBase64}" class="logo" alt="" />`
    : '';

  // --- Bloque del comprobante ---
  const fechaKey = cbte.fiscal
    ? (sale.fecha_facturacion || sale.invoice_sent_at || sale.fecha_venta || sale.created_at)
    : (sale.fecha_venta || sale.created_at);
  const fechaTxt = formatDateOnlyValue(String(fechaKey || '').length > 10 ? getOperationDateKey(fechaKey) : fechaKey);
  const horaTxt = formatOperationTime(cbte.fiscal ? (sale.invoice_sent_at || sale.created_at) : sale.created_at);
  const numero = cbte.fiscal
    ? formatComprobanteNumero(sale.numero_factura)
    : (internalNumber || String(sale.id || '').slice(0, 8).toUpperCase());

  const cbteTitulo = cbte.fiscal
    ? `${e(cbte.label)} ${e(cbte.letra)}`
    : 'TICKET';
  const cbteSub = cbte.fiscal
    ? `ORIGINAL${cbte.codigo ? ` (Cód. ${String(cbte.codigo).padStart(2, '0')})` : ''}`
    : e(String(subHeader).toUpperCase());

  // --- Receptor ---
  let clienteHtml = '';
  const cliName = cli?.full_name || '';
  const cliDoc = cli?.doc_number && Number(onlyDigits(cli.doc_number)) > 0 ? String(cli.doc_number) : '';
  const cliDocType = cli?.doc_type || (onlyDigits(cliDoc).length === 11 ? 'CUIT' : 'DNI');
  const showCliente = cbte.fiscal || cfg.showClientInfo !== false;
  if (showCliente) {
    const lines = [];
    if (cbte.fiscal && (cbte.letra === 'B' || cbte.letra === 'C') && (!cli || /consumidor final/i.test(cli?.tax_condition || 'Consumidor Final'))) {
      lines.push('<div class="bold">A CONSUMIDOR FINAL</div>');
    }
    if (cliDoc) lines.push(`<div>${e(cliDocType)} ${e(cliDoc)}</div>`);
    if (cliName && !isGenericConsumidorFinal(cli)) lines.push(`<div>${e(String(cliName).toUpperCase())}</div>`);
    if (cli?.tax_condition && !/consumidor final/i.test(cli.tax_condition)) lines.push(`<div>${e(String(cli.tax_condition).toUpperCase())}</div>`);
    if (cli?.address) lines.push(`<div>${e(cli.address)}</div>`);
    if (!lines.length && !cbte.fiscal) lines.push('<div>CONSUMIDOR FINAL</div>');
    if (sellerName) lines.push(`<div>Vend: ${e(sellerName)}</div>`);
    clienteHtml = lines.length ? `<div class="sep"></div><div class="block">${lines.join('')}</div>` : '';
  }

  // --- Ítems ---
  // Prorrateo del descuento/recargo de cabecera solo para el IVA (no se tocan
  // los importes de línea, que son lo que el cliente ve en góndola).
  const allItems = Array.isArray(items) ? items : [];
  const billables = selectBillableItems(allItems);
  const sumGross = round2(billables.reduce((acc, it) => acc + lineGross(it), 0));

  const itemsHtml = allItems.map((it) => {
    const name = e(getSaleItemDisplayName(it));
    if (it?.combo_role === 'component') {
      return `<div class="item-comp">&nbsp;&nbsp;· ${formatQty(it.quantity)} ${name}</div>`;
    }
    const qty = Number(it.quantity || 0);
    const gross = lineGross(it);
    const unit = qty ? gross / qty : Number(it.unit_price || 0);
    const rate = getItemIvaPercent(it, 21);
    const code = it?.products?.barcode || it?.products?.sku || it?.barcode || it?.sku || '';
    // Descuento por producto: se muestra el precio de lista y el descuento de la
    // línea; el importe de la línea sigue siendo el neto (subtotal).
    const descItem = lineItemDiscount(it);
    if (descItem) {
      return `
      <div class="item">
        <div>${formatQty(qty)} x $${formatTicketMoney(descItem.lista)}${muestraAlicuota ? ` (${formatRate(rate)}%)` : ''}</div>
        <div class="desc">${name}</div>
        <div class="row"><span>Desc. ${formatRate(descItem.pct)}%</span><span>-${formatTicketMoney(descItem.monto)}</span></div>
        <div class="row"><span>${e(code)}</span><span>${formatTicketMoney(gross)}</span></div>
      </div>`;
    }
    return `
      <div class="item">
        <div>${formatQty(qty)} x $${formatTicketMoney(unit)}${muestraAlicuota ? ` (${formatRate(rate)}%)` : ''}</div>
        <div class="desc">${name}</div>
        <div class="row"><span>${e(code)}</span><span>${formatTicketMoney(gross)}</span></div>
      </div>`;
  }).join('');
  const ahorroItems = round2(allItems
    .filter((it) => it?.combo_role !== 'component')
    .reduce((acc, it) => acc + (lineItemDiscount(it)?.monto || 0), 0));

  const listHeader = `
    <div class="muted-head">
      <div>Cant x Precio${muestraAlicuota ? ' (%IVA)' : ''}</div>
      <div>Descripción</div>
    </div>`;

  // --- Ajustes / totales ---
  const ajuste = round2(total - sumGross);
  let ajustesHtml = '';
  if (cfg.showDiscounts !== false && sumGross > 0 && Math.abs(ajuste) >= 0.01) {
    ajustesHtml = `
      <div class="row"><span>Subtotal</span><span>${formatTicketMoney(sumGross)}</span></div>
      <div class="row"><span>${ajuste < 0 ? 'Descuento' : 'Recargo'}</span><span>${ajuste < 0 ? '-' : ''}${formatTicketMoney(Math.abs(ajuste))}</span></div>`;
  }

  if (ahorroItems >= 0.01) {
    const ahorroTotal = round2(ahorroItems + (ajuste < 0 ? Math.abs(ajuste) : 0));
    ajustesHtml += `
      <div class="row"><span>Ahorro total</span><span>${formatTicketMoney(ahorroTotal)}</span></div>`;
  }

  const iva = (discrimina || transparencia) ? buildIvaDesglose({ sale, items: allItems, total }) : null;

  let discriminadoHtml = '';
  if (discrimina && iva) {
    const exento = round2(total - iva.totalNeto - iva.totalIva);
    discriminadoHtml = `
      <div class="row"><span>Neto gravado</span><span>${formatTicketMoney(iva.totalNeto)}</span></div>
      ${iva.rows.map((r) => `<div class="row"><span>IVA ${formatRate(r.rate)}%</span><span>${formatTicketMoney(r.iva)}</span></div>`).join('')}
      ${exento >= 0.01 ? `<div class="row"><span>No gravado / Exento</span><span>${formatTicketMoney(exento)}</span></div>` : ''}`;
  }

  let transparenciaHtml = '';
  if (transparencia && iva) {
    const porAlicuota = cfg.showTaxPerRate && iva.rows.length > 1;
    transparenciaHtml = `
      <div class="block">
        <div>RÉGIMEN DE TRANSPARENCIA</div>
        <div>FISCAL AL CONSUMIDOR (LEY 27.743)</div>
        ${porAlicuota
          ? iva.rows.map((r) => `<div class="row"><span>IVA Contenido ${formatRate(r.rate)}%</span><span>${formatTicketMoney(r.iva)}</span></div>`).join('')
          : ''}
        <div class="row"><span>IVA Contenido</span><span>${formatTicketMoney(iva.totalIva)}</span></div>
      </div>`;
  }

  // Factura A a monotributista (Ley 27.618) o forzada por config.
  let leyendaMonoHtml = '';
  const receptorMono = /monotrib/i.test(cli?.tax_condition || '');
  if (cbte.fiscal && cbte.letra === 'A' && (receptorMono || cfg.showMonotributoLegend)) {
    leyendaMonoHtml = `<div class="legend">El crédito fiscal discriminado en el presente comprobante sólo podrá ser computado a efectos del Régimen de Sostenimiento e Inclusión Fiscal para Pequeños Contribuyentes de la Ley N° 27.618.</div>`;
  }

  // --- Pagos ---
  let pagosHtml = '';
  if (cfg.showPaymentMethod !== false) {
    const list = Array.isArray(payments) && payments.length
      ? payments
      : [{ label: sale.payment_method || 'Efectivo', amount: total }];
    pagosHtml = `
      <div class="block">
        <div>RECIBIMOS</div>
        ${list.map((p) => `<div class="row"><span>${e(String(p.label || '').toUpperCase())}</span><span>${formatTicketMoney(p.amount)}</span></div>`).join('')}
        ${change !== null && change !== undefined ? `<div class="row"><span>SU VUELTO</span><span>${formatTicketMoney(change)}</span></div>` : ''}
      </div>`;
  }

  // --- CAE + QR ---
  let caeHtml = '';
  if (cbte.fiscal) {
    caeHtml = `
      <div class="sep"></div>
      <div class="ref-title">-REFERENCIA ELECTRÓNICA DEL COMPROBANTE-</div>
      <div class="row"><span>CAE ${e(sale.cae)}</span><span>VENC ${e(formatDateOnlyValue(sale.cae_vencimiento))}</span></div>
      ${qrDataUrl && cfg.showQr !== false ? `<div class="qr-wrap"><img src="${qrDataUrl}" class="qr" alt="QR ARCA" /></div>` : ''}`;
  }

  const footer = cfg.footerText
    ? `<div class="sep"></div><div class="footer">${e(cfg.footerText)}</div>`
    : '';

  return `<!doctype html>
<html>
<head>
<meta charset="utf-8">
<title>${e(cbteTitulo)} ${e(numero)}</title>
<style>
  @page { size: ${w.page} auto; margin: 0; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; background: #fff; }
  body {
    width: ${w.page};
    padding: ${w.pad};
    font-family: 'Courier New', Courier, monospace;
    font-size: ${w.font};
    line-height: 1.25;
    color: #000;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .ticket { ${w.thermal ? '' : 'max-width: 120mm; margin: 0 auto;'} }
  .center { text-align: center; }
  .bold { font-weight: bold; }
  .row { display: flex; flex-wrap: wrap; justify-content: space-between; column-gap: 6px; }
  .row > span:last-child { white-space: nowrap; text-align: right; }
  .sep { border-top: 1px dashed #000; margin: 5px 0; }
  .block { margin: 4px 0; }
  .logo { display: block; margin: 0 auto 4px; max-width: 70%; max-height: 22mm; object-fit: contain; }
  .emp-title { font-size: 1.3em; font-weight: bold; text-transform: uppercase; }
  .cbte-title { font-size: 1.25em; font-weight: bold; letter-spacing: 1px; }
  .ref-title { font-size: 0.9em; font-weight: bold; text-align: center; }
  .muted-head { margin-bottom: 2px; }
  .item { margin: 3px 0; }
  .item .desc { text-transform: uppercase; word-break: break-word; }
  .item-comp { font-size: 0.9em; }
  .total { font-size: 1.5em; font-weight: bold; margin: 4px 0; }
  .legend { border: 1px solid #000; padding: 3px; margin: 4px 0; font-size: 0.85em; text-align: justify; }
  .qr-wrap { text-align: center; margin: 6px 0 2px; }
  .qr { width: ${w.qr}; height: ${w.qr}; image-rendering: pixelated; }
  .footer { text-align: center; white-space: pre-wrap; text-transform: uppercase; }
</style>
</head>
<body><div class="ticket">
  ${logo}
  <div class="emp-title">${e(headerTitle)}</div>
  ${headerLines.map((l) => `<div>${l}</div>`).join('')}
  <div class="sep"></div>
  <div class="center cbte-title">${cbteTitulo}</div>
  <div class="center">${cbteSub}</div>
  <div class="sep"></div>
  <div class="row"><span>${e(fechaTxt)} ${e(horaTxt)}</span><span>Nº ${e(numero)}</span></div>
  ${siteName ? `<div>Suc: ${e(siteName)}</div>` : ''}
  ${clienteHtml}
  <div class="sep"></div>
  ${listHeader}
  <div class="sep"></div>
  ${itemsHtml}
  <div class="sep"></div>
  ${ajustesHtml}
  ${discriminadoHtml}
  <div class="row total"><span>TOTAL</span><span>${formatTicketMoney(total)}</span></div>
  ${transparenciaHtml}
  ${leyendaMonoHtml}
  ${pagosHtml}
  ${caeHtml}
  ${footer}
</div></body>
</html>`;
};
